import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Form, message, Select } from "antd";
import { data, validation } from "../../../constants";
import { ButtonWithIcon } from "../../atoms/Button";
import { Radio } from "../../atoms/Radio";
import { InputWithLabel } from "../../molecules/InputWithLabel";
import { RadioWithLabel } from "../../molecules/RadioWithLabel";
import { SelectWithLabel } from "../../molecules/SelectWithLabel";
import {
  ErrorText,
  FormItem,
  InputField,
  InputTitle,
  TextAreaTitle,
} from "./elements";
import { UploadOutlined } from "@ant-design/icons";
import { TextArea } from "../../atoms/TextArea";
import { Loader } from "../../atoms/Loader";
import { Props } from "./types";
import { setResumeFileName } from "../../../redux/applicant/actions";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { hasExtension } from "./../../../utils/helpers/file";

const ApplicantPreScreeningForm: FC<Props> = (props) => {
  const {
    onSubmitPreScreening,
    preScreeningForm,
    isUploading,
    dispatchUploadFile,
    showUploadResumeError,
    setShowUploadResumeError,
    onLeavePreScreeningForm,
    applicantInfo,
  } = props;
  const requiredRule: object = validation.requiredRuleWithCommonMessage;
  const stringRules: object = validation.stringRules;
  const dispatch = useDispatch();
  const inputFile = useRef<HTMLInputElement>(null);
  const resumeFileName = useSelector(
    (state: RootStateOrAny) => state.applicant.resumeFileName
  );
  const dispatchSetResumeFileName = useCallback(
    (value: RootStateOrAny) => dispatch(setResumeFileName(value)),
    [dispatch]
  );
  useEffect(() => {
    return () => {
      // @ts-ignore
      onLeavePreScreeningForm();
      setShowUploadResumeError(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setShowUploadResumeError]);

  const setYearsOfExperienceForm = (e: any) => {
    preScreeningForm.setFieldsValue({ years_of_experience: e });
  };

  const openFileUpload = (e: any) => {
    e.preventDefault();
    inputFile?.current?.click();
  };

  const onUploadResume = async (e: any) => {
    const targetFile = e.target.files[0];

    if (!hasExtension(targetFile, validation.validFileExtensions)) {
      message.error("File is not valid");
      return dispatchSetResumeFileName("");
    }

    if (targetFile) {
      if (targetFile.size > data.maximumResumeFileSize) {
        message.error("File size too large");
        return dispatchSetResumeFileName("");
      } else {
        await dispatchUploadFile(targetFile, targetFile.name);
        setShowUploadResumeError(false);
        return dispatchSetResumeFileName(targetFile.name);
      }
    }

    return dispatchSetResumeFileName("");
  };

  const onChangeRecentWork = (e: any) => {
    preScreeningForm.setFieldsValue({ recent_work: e.target.value });
  };

  const onChangeToolsUsed = (e: any) => {
    preScreeningForm.setFieldsValue({ tools_used: e.target.value });
  };

  const [valuePersistor, setValuePersistor] = useState({
    reason_of_leaving: "",
    willing_to_relocate: "",
    agile_experience: "",
    work_office: "",
    platform_applied: "",
  });

  const onChangeValue = (prop: string, value: string) => {
    setValuePersistor({ ...valuePersistor, [prop]: value });
  };

  useEffect(() => {
    setValuePersistor({
      reason_of_leaving: preScreeningForm.getFieldValue("reason_of_leaving"),
      willing_to_relocate: preScreeningForm.getFieldValue(
        "willing_to_relocate"
      ),
      agile_experience: preScreeningForm.getFieldValue("agile_experience"),
      work_office: preScreeningForm.getFieldValue("work_office"),
      platform_applied: preScreeningForm.getFieldValue("platform_applied"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Form
      onFinish={onSubmitPreScreening}
      validateMessages={validation.validateMessages}
      form={preScreeningForm}
      style={{ paddingBottom: "20px" }}
      scrollToFirstError={{ behavior: "smooth" }}
    >
      <FormItem name="willing_to_relocate" rules={[requiredRule]}>
        <span id={"willing_to_relocate"}>
          <RadioWithLabel
            label="This position may become an office-based job role. If currently living outside Cebu City, are you willing to relocate at your own expense once improvements allow full office-based operations?"
            radioItems={
              <>
                <Radio radioValue={"Yes"}>Yes</Radio>
                <Radio radioValue={"No"}>No</Radio>
                <Radio radioValue={"Not Applicable"}>Not Applicable</Radio>
              </>
            }
            onChange={(e: any) =>
              onChangeValue("willing_to_relocate", e.target.value)
            }
            value={valuePersistor.willing_to_relocate}
          />
        </span>
      </FormItem>
      <FormItem name="years_of_experience" rules={[requiredRule]}>
        <span id={"years_of_experience"}>
          <SelectWithLabel
            label="Indicate your years of experience for position applied"
            selectOnChange={setYearsOfExperienceForm}
            optionItems={
              <>
                <Select.Option value="1 year or less">
                  1 year or less
                </Select.Option>
                <Select.Option value="2 years to 4 years">
                  2 years to 4 years
                </Select.Option>
                <Select.Option value="5+ years">5+ years</Select.Option>
              </>
            }
            selectDefaultValue={applicantInfo?.yearsOfExperience || undefined}
            getPopupContainer={(trigger: any) => trigger.parentNode}
          />
        </span>
      </FormItem>
      <FormItem name="recent_work" label="This field" rules={[stringRules]}>
        <InputField>
          <TextAreaTitle>
            Summary of recent work experiences (Indicate company name, inclusive
            dates, job title, scope of work, daily routine)
          </TextAreaTitle>
          <TextArea
            onChange={onChangeRecentWork}
            defaultValue={applicantInfo?.recentWork || undefined}
          />
        </InputField>
      </FormItem>
      <FormItem
        name="reason_of_leaving"
        label="This field"
        rules={[stringRules]}
      >
        <span id="reason_of_leaving">
          <InputWithLabel
            type="default"
            label="Reason for leaving previous job"
            value={valuePersistor.reason_of_leaving}
            onChange={(e) => {
              onChangeValue("reason_of_leaving", e.target.value);
            }}
          />
        </span>
      </FormItem>
      <FormItem name="tools_used" label="This field" rules={[stringRules]}>
        <InputField>
          <TextAreaTitle>Tools you used in carrying out your job</TextAreaTitle>
          <TextArea
            onChange={onChangeToolsUsed}
            defaultValue={applicantInfo?.toolsUsed || undefined}
          />
        </InputField>
      </FormItem>
      <FormItem name="agile_experience" rules={[requiredRule]}>
        <span id="agile_experience">
          <RadioWithLabel
            label="Do you have experience working with scrum agile team?"
            radioItems={
              <>
                <Radio radioValue={"Yes"}>Yes</Radio>
                <Radio radioValue={"No"}>No</Radio>
              </>
            }
            value={valuePersistor.agile_experience}
            onChange={(e: any) =>
              onChangeValue("agile_experience", e.target.value)
            }
          />
        </span>
      </FormItem>
      <FormItem name="work_office" rules={[requiredRule]}>
        <span id="work_office">
          <RadioWithLabel
            label="Are you willing to work in the office once everything goes back to normal?"
            radioItems={
              <>
                <Radio radioValue={"Yes"}>Yes</Radio>
                <Radio radioValue={"No"}>No</Radio>
              </>
            }
            value={valuePersistor.work_office}
            onChange={(e: any) => onChangeValue("work_office", e.target.value)}
          />
        </span>
      </FormItem>
      <FormItem name="platform_applied" rules={[requiredRule]}>
        <span id="platform_applied">
          <RadioWithLabel
            label="Which platform did you use to send your resume?"
            radioItems={
              <>
                <Radio radioValue={"Facebook"}>Facebook</Radio>
                <br />
                <Radio radioValue={"Mynimo"}>Mynimo</Radio>
                <br />
                <Radio radioValue={"Jobstreet"}>Jobstreet</Radio>
                <br />
                <Radio radioValue={"LinkedIn"}>LinkedIn</Radio>
              </>
            }
            value={valuePersistor.platform_applied}
            onChange={(e: any) =>
              onChangeValue("platform_applied", e.target.value)
            }
          />
        </span>
      </FormItem>
      <InputTitle>
        Upload updated resume
        <br />• only .doc, .docx, .pdf, or .rtf file extensions are allowed
        <br />• file must be less than 900kb
      </InputTitle>
      <input
        type="file"
        onChange={onUploadResume}
        ref={inputFile}
        accept={validation.resumeFileExtensions}
      />
      <p>{resumeFileName}</p>
      {isUploading ? (
        <Loader />
      ) : (
        <ButtonWithIcon icon={<UploadOutlined />} onClick={openFileUpload}>
          Upload
        </ButtonWithIcon>
      )}
      {showUploadResumeError && (
        <ErrorText className="ant-form-item-explain ant-form-item-explain-error">
          Please upload your resume
        </ErrorText>
      )}
    </Form>
  );
};

export default ApplicantPreScreeningForm;
